// frontend/src/components/dashboard/ArbolFamilias.jsx
// SIDEBAR REDIMENSIONABLE CON ÁRBOL DE FAMILIAS Y EQUIPOS
// REPLICA EXACTA DE arbol_familias.html CON DATOS REALES

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// SIDEBAR (EXACTO AL ORIGINAL CSS #sidebar)
const Sidebar = styled.div`
  width: 100%; /* Toma el ancho completo del contenedor padre */
  height: 100%; /* Toma toda la altura disponible */
  background-color: ${({ theme }) => theme.colors.bgSecundario};
  border-right: 1px solid ${({ theme }) => theme.colors.borderDefault};
  overflow: hidden; /* Sin overflow en el contenedor principal */
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: flex;
  flex-direction: column;
  
  /* Scrollbar personalizado (igual al custom.css original) */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollThumb} ${({ theme }) => theme.colors.scrollTrack};
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.scrollTrack};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollThumb};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.scrollThumbHover};
  }
`;

// HEADER DEL SIDEBAR CON TÍTULO Y BOTONES (EXACTO AL HTML) - FIJO
const SidebarHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.surface};
  padding: 10px 12px; /* Reducido de 14px 15px para más espacio */
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0; /* Mantener fijo el header */
  z-index: 10; /* Asegurar que quede por encima */
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.onPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
  
  i {
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

const SidebarButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const SidebarButton = styled.button`
  background: ${({ theme }) => theme.colors.transparent};
  border: 1px solid ${({ theme }) => theme.colors.onPrimary};
  color: ${({ theme }) => theme.colors.onPrimary};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  transition: ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    border-color: ${({ theme }) => theme.colors.onPrimary};
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  i {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

// CONTENEDOR SCROLLEABLE PARA EL ÁRBOL
const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem; /* Reducido de 1rem para más espacio */
  
  /* Scrollbar personalizado (igual al custom.css original) */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollThumb} ${({ theme }) => theme.colors.scrollTrack};
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.scrollTrack};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollThumb};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.scrollThumbHover};
  }
`;

// ACCORDION PARA FAMILIAS (EXACTO AL BOOTSTRAP ACCORDION)
const AccordionContainer = styled.div`
  /* Contenedor del árbol de familias */
`;

const AccordionItem = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 4px;
  margin-bottom: 2px; /* Reducido de 4px a 2px para más compacidad */
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
`;

const AccordionHeader = styled.div`
  background: ${({ theme }) => theme.colors.surfaceHover};
  padding: 6px 10px; /* Reducido de 8px 12px para mayor compacidad */
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
  }
`;

const AccordionTitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const AccordionTitle = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const AccordionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EquipoCount = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
`;

const AccordionIcon = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})`
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => isOpen ? 'rotate(90deg)' : 'rotate(0deg)'};
  font-size: 12px;
`;

const AccordionBody = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})`
  display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
  padding: 2px 0; /* Reducido de 5px a 2px para mayor compacidad */
`;

// LISTA DE EQUIPOS DENTRO DE CADA FAMILIA
const EquiposList = styled.div`
  /* Sin padding adicional, los equipos van directo */
`;

const EquipoItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected'
})`
  padding: 6px 12px; /* Reducido de 8px 15px a 6px 12px */
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ isSelected, theme }) => 
    isSelected ? theme.colors.primary : theme.colors.transparent};
  color: ${({ isSelected, theme }) => 
    isSelected ? theme.colors.onPrimary : theme.colors.txtPrincipal};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ isSelected, theme }) => 
      isSelected ? theme.colors.primaryHover : theme.colors.surfaceHover};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const EquipoInfo = styled.div`
  display: flex;
  align-items: center; /* Cambio a una sola línea */
  gap: 8px; /* Espacio entre interno y descripción */
  overflow: hidden; /* Para evitar desbordamiento */
`;

const EquipoInterno = styled.span`
  font-weight: 600;
  font-size: 13px;
  min-width: 60px; /* Ancho mínimo para el interno */
  flex-shrink: 0; /* No se encoge */
`;

const EquipoDescripcion = styled.span`
  font-size: 12px;
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap; /* Una sola línea */
  flex: 1; /* Toma el espacio restante */
`;

const EquipoDetails = styled.div`
  font-size: 11px;
  opacity: 0.7;
  margin-top: 2px;
`;

// ESTADO DE CARGA
const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-style: italic;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.colors.danger};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.danger};
  border-radius: 4px;
  margin: 10px 0;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 15px;
  color: ${({ theme }) => theme.colors.txtSecondary};
  font-style: italic;
  font-size: 12px;
`;

// COMPONENTE PRINCIPAL
const ArbolFamilias = ({ 
  onSelectEquipo, 
  onOpenFiltros, 
  onOpenBusqueda,
  familias = [],
  equipos = [],
  loading = false,
  error = null,
  selectedEquipoId = null // Agregar prop para equipo seleccionado externamente
}) => {
  const navigate = useNavigate();
  const [openFamilias, setOpenFamilias] = useState({});
  const [internalSelectedEquipoId, setInternalSelectedEquipoId] = useState(null);

  // VALIDAR QUE equipos SEA UN ARRAY ANTES DE USAR reduce - Optimizado con useMemo
  const validEquipos = useMemo(() => Array.isArray(equipos) ? equipos : [], [equipos]);
  const validFamilias = useMemo(() => Array.isArray(familias) ? familias : [], [familias]);
  
  // Agrupar equipos por familia
  const equiposPorFamilia = validEquipos.reduce((acc, equipo) => {
    // En la API, familia es directamente el ID numérico, no un objeto
    const familiaId = equipo.familia || 'sin_familia';
    if (!acc[familiaId]) {
      acc[familiaId] = [];
    }
    acc[familiaId].push(equipo);
    return acc;
  }, {});

  // Expandir automáticamente la familia cuando se selecciona un equipo externamente
  useEffect(() => {
    if (selectedEquipoId) {
      setInternalSelectedEquipoId(selectedEquipoId);
      // Encontrar el equipo y expandir su familia
      const equipo = validEquipos.find(eq => eq.id === selectedEquipoId);
      if (equipo && equipo.familia) {
        // COLAPSAR TODAS LAS FAMILIAS Y ABRIR SOLO LA DEL EQUIPO SELECCIONADO
        setOpenFamilias({ [equipo.familia]: true });
        console.log('🌳 Familia expandida para equipo:', equipo.interno, '- Familia ID:', equipo.familia);
      }
    }
  }, [selectedEquipoId, validEquipos]);

  // Toggle accordion familia - CONTRAER OTRAS FAMILIAS AL ABRIR UNA
  const toggleFamilia = (familiaId) => {
    setOpenFamilias(prev => {
      // Si la familia ya está abierta, la cerramos
      if (prev[familiaId]) {
        return { ...prev, [familiaId]: false };
      }
      // Si no está abierta, cerramos todas las demás y abrimos solo esta
      else {
        return { [familiaId]: true }; // Solo esta familia estará abierta
      }
    });
  };

  // Seleccionar equipo
  const handleSelectEquipo = (equipo) => {
    setInternalSelectedEquipoId(equipo.id);
    if (onSelectEquipo) {
      onSelectEquipo(equipo);
    }
  };

  // Lista aplanada con TODOS los equipos, en el mismo orden en que se
  // renderizan las familias (familias con nombre primero, luego "sin familia").
  // Es el orden "canónico" de la flota, exista o no una familia abierta:
  // sirve tanto para navegar dentro de la familia visible como para saltar
  // a la familia siguiente/anterior al llegar a un borde.
  const equiposOrdenTotal = useMemo(() => {
    const resultado = [];

    validFamilias.forEach(familia => {
      const familiaEquipos = equiposPorFamilia[familia.id] || [];
      if (familiaEquipos.length > 0) resultado.push(...familiaEquipos);
    });

    if (equiposPorFamilia.sin_familia) {
      resultado.push(...equiposPorFamilia.sin_familia);
    }

    return resultado;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validFamilias, equiposPorFamilia]);

  // Encuentra a qué familia (o 'sin_familia') pertenece un equipo, para poder
  // expandirla cuando la navegación con flechas cae en una familia colapsada.
  const encontrarFamiliaDeEquipo = (equipo) => {
    if (!equipo) return null;
    return equipo.familia || 'sin_familia';
  };

  // Navegar al equipo anterior/siguiente en el orden total de la flota (flechas ↑/↓).
  // Al cruzar de una familia a otra, expande automáticamente la familia destino
  // (acordeón exclusivo) para que el equipo seleccionado quede visible.
  const seleccionarEquipoAdyacente = (direccion) => {
    const listaActual = equiposOrdenTotal;
    if (listaActual.length === 0) return;

    // Caso base: nada seleccionado todavía -> seleccionar el primero
    if (!internalSelectedEquipoId) {
      handleSelectEquipo(listaActual[0]);
      return;
    }

    const indiceActual = listaActual.findIndex(eq => eq.id === internalSelectedEquipoId);
    if (indiceActual === -1) {
      handleSelectEquipo(listaActual[0]);
      return;
    }

    const nuevoIndice = indiceActual + direccion;
    if (nuevoIndice < 0 || nuevoIndice >= listaActual.length) {
      return; // ya está en el primero/último equipo de toda la flota
    }

    const siguienteEquipo = listaActual[nuevoIndice];
    const familiaDestino = encontrarFamiliaDeEquipo(siguienteEquipo);
    const familiaActual = encontrarFamiliaDeEquipo(listaActual[indiceActual]);
    if (familiaDestino !== familiaActual) {
      setOpenFamilias({ [familiaDestino]: true });
    }

    handleSelectEquipo(siguienteEquipo);
  };

  // Navegación con flechas ↑/↓ independiente del foco del teclado: mientras se
  // esté en el Dashboard, subir/bajar cambia el equipo seleccionado al
  // anterior/siguiente en el orden visible del árbol. Se ignora si el foco
  // está en un campo editable para no romper la edición normal de formularios.
  useEffect(() => {
    const esCampoEditable = (elemento) => {
      if (!elemento) return false;
      const tag = elemento.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        elemento.isContentEditable
      );
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      if (esCampoEditable(document.activeElement)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      event.preventDefault();
      seleccionarEquipoAdyacente(event.key === 'ArrowDown' ? 1 : -1);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equiposOrdenTotal, internalSelectedEquipoId]);

  // Navegar a formulario de nuevo equipo
  const handleAgregarEquipo = () => {
    navigate('/equipos/crear');
  };

  const handleAbrirGrilla = () => {
    navigate('/flota/grilla');
  };

  // Renderizar contenido según estado
  if (loading) {
    return (
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>
            <i className="bi bi-arrow-repeat"></i> Flota de Equipos
          </SidebarTitle>
        </SidebarHeader>
        <SidebarContent>
          <LoadingMessage>Cargando equipos...</LoadingMessage>
        </SidebarContent>
      </Sidebar>
    );
  }

  if (error) {
    return (
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>
            <i className="bi bi-exclamation-triangle"></i> Flota de Equipos
          </SidebarTitle>
        </SidebarHeader>
        <SidebarContent>
          <ErrorMessage>{error}</ErrorMessage>
        </SidebarContent>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      {/* HEADER CON TÍTULO Y BOTONES - FIJO */}
      <SidebarHeader>
        <SidebarTitle>
          <i className="bi bi-diagram-3"></i> Flota de Equipos
        </SidebarTitle>
        <SidebarButtons>
          <SidebarButton onClick={handleAgregarEquipo} title="Agregar nuevo equipo">
            <i className="bi bi-plus"></i>
          </SidebarButton>
          <SidebarButton onClick={handleAbrirGrilla} title="Ver flota en grilla">
            <i className="bi bi-table"></i>
          </SidebarButton>
          <SidebarButton onClick={onOpenFiltros} title="Filtros">
            <i className="bi bi-funnel"></i>
          </SidebarButton>
          <SidebarButton onClick={onOpenBusqueda} title="Buscar">
            <i className="bi bi-search"></i>
          </SidebarButton>
        </SidebarButtons>
      </SidebarHeader>

      {/* CONTENIDO SCROLLEABLE */}
      <SidebarContent>
        <AccordionContainer>
          {validFamilias && validFamilias.length > 0 ? (
            validFamilias
              .map(familia => {
                const familiaEquipos = equiposPorFamilia[familia.id] || [];
                return { familia, familiaEquipos };
              })
              .filter(({ familiaEquipos }) => familiaEquipos.length > 0) // Solo mostrar familias con equipos
              .map(({ familia, familiaEquipos }) => {
                const isOpen = openFamilias[familia.id] || false;
                
                return (
                  <AccordionItem key={familia.id}>
                    <AccordionHeader onClick={() => toggleFamilia(familia.id)}>
                      <AccordionTitleSection>
                        <AccordionTitle>
                          {familia.nombre}
                        </AccordionTitle>
                      </AccordionTitleSection>
                      <AccordionControls>
                        <EquipoCount>{familiaEquipos.length}</EquipoCount>
                        <AccordionIcon isOpen={isOpen}>▶</AccordionIcon>
                      </AccordionControls>
                    </AccordionHeader>
                    <AccordionBody isOpen={isOpen}>
                      <EquiposList>
                        {familiaEquipos.length > 0 ? (
                          familiaEquipos.map(equipo => (
                            <EquipoItem
                              key={equipo.id}
                              isSelected={internalSelectedEquipoId === equipo.id}
                              onClick={() => handleSelectEquipo(equipo)}
                            >
                              <EquipoInfo>
                                <EquipoInterno>
                                  {equipo.interno || `EQ-${equipo.id}`}
                                </EquipoInterno>
                                <EquipoDescripcion>
                                  {equipo.descripcion || equipo.nombre || 'Sin descripción'}
                                </EquipoDescripcion>
                              </EquipoInfo>
                            </EquipoItem>
                          ))
                        ) : (
                          <EmptyMessage>Sin equipos en esta familia</EmptyMessage>
                        )}
                      </EquiposList>
                    </AccordionBody>
                  </AccordionItem>
                );
              })
          ) : (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              {loading ? 'Cargando equipos...' : 
               validFamilias.length === 0 ? 'No hay familias disponibles' :
               validEquipos.length === 0 ? 'No hay equipos disponibles' :
               'No hay datos para mostrar'}
            </div>
          )}

          {/* EQUIPOS SIN FAMILIA */}
          {equiposPorFamilia.sin_familia && equiposPorFamilia.sin_familia.length > 0 && (
            <AccordionItem>
              <AccordionHeader onClick={() => toggleFamilia('sin_familia')}>
                <AccordionTitleSection>
                  <AccordionTitle>
                    Sin Familia
                  </AccordionTitle>
                </AccordionTitleSection>
                <AccordionControls>
                  <EquipoCount>{equiposPorFamilia.sin_familia.length}</EquipoCount>
                  <AccordionIcon isOpen={openFamilias.sin_familia}>▶</AccordionIcon>
                </AccordionControls>
              </AccordionHeader>
              <AccordionBody isOpen={openFamilias.sin_familia}>
                <EquiposList>
                  {equiposPorFamilia.sin_familia.map(equipo => (
                    <EquipoItem
                      key={equipo.id}
                      isSelected={selectedEquipoId === equipo.id}
                      onClick={() => handleSelectEquipo(equipo)}
                    >
                      <EquipoInfo>
                        <EquipoInterno>
                          {equipo.interno || `EQ-${equipo.id}`}
                        </EquipoInterno>
                        <EquipoDescripcion>
                          {equipo.descripcion || equipo.nombre || 'Sin descripción'}
                        </EquipoDescripcion>
                        <EquipoDetails>
                          {equipo.sucursal?.nombre && `📍 ${equipo.sucursal.nombre}`}
                          {equipo.estado?.nombre && ` • 🔧 ${equipo.estado.nombre}`}
                        </EquipoDetails>
                      </EquipoInfo>
                    </EquipoItem>
                  ))}
                </EquiposList>
              </AccordionBody>
            </AccordionItem>
          )}
        </AccordionContainer>
      </SidebarContent>
    </Sidebar>
  );
};

export default ArbolFamilias;
